// src/services/gameSounds.js
//
// GAME SOUNDS YOU UPLOAD (Nov 2026). The games used to have every sound
// written into their code as a Storage link, so changing one meant editing
// code and deploying. Now an admin uploads MP3s in Layout Builder →
// 🎵 Game sounds, and every game plays them.
//
// Saved in rewardSettings/main.sounds (already readable by everyone signed
// in, writable only by admins — no rules change):
//
//   sounds = {
//     all:   { correct: { url, name, volume }, wrong: {...}, ... }
//     games: { diamondTap: { bonusBomb: {...} }, ... }   one game only
//   }
//   A slot set to { none: true } is silent on purpose.
//
// Which sound plays: that game's own → the "every game" one → the game's
// built-in sound (what it always played). Nothing uploaded = nothing changes.

import { playFast, preloadFast } from "../components/quizzes/sfx";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

/** The moments a sound can be set for. */
export const SOUND_SLOTS = [
  { id: "correct", group: "main", label: "Right answer", hint: "A question answered correctly" },
  { id: "wrong", group: "main", label: "Wrong answer", hint: "A question answered wrong" },
  { id: "roundDone", group: "main", label: "Questions finished — good job!", hint: "The “Questions done” screen, before the bonus round", end: true },
  { id: "bonusGood", group: "bonus", label: "Right item tapped", hint: "A diamond, the plane’s target, a right-answer bubble" },
  { id: "bonusPower", group: "bonus", label: "Extra points / power-up", hint: "Gold gem, ⭐ star, ⏰ clock, and reaching ×2 / ×3 combos" },
  { id: "bonusBomb", group: "bonus", label: "Bomb tapped", hint: "💣 in any bonus round" },
  { id: "bonusWrong", group: "bonus", label: "Wrong item tapped", hint: "A wrong-answer bubble, a decoy in the plane round" },
  { id: "gameOver", group: "bonus", label: "Game over — final score", hint: "The finish screen with stars, scores and coins", end: true },
];

/** Games that can have their own sounds (the "one game only" list). */
export const SOUND_GAMES = [
  { id: "multipleChoice", group: "main", label: "Multiple Choice" },
  { id: "dragDrop", group: "main", label: "Drag and Drop" },
  { id: "talkChoice", group: "main", label: "Talk Choice" },
  { id: "typingQuiz", group: "main", label: "Typing Quiz (and the Word Blaster typing bonus)" },
  { id: "countPop", group: "main", label: "Count Pop" },
  { id: "memoryMatch", group: "main", label: "Memory Match" },
  { id: "sortingConveyor", group: "main", label: "Sorting Conveyor" },
  { id: "universalMath", group: "main", label: "Math game" },
  { id: "diamondTap", group: "bonus", label: "Diamond Drop bonus" },
  { id: "sideScroller", group: "bonus", label: "Plane, rocket, car & fish bonus" },
  { id: "bubbleBonus", group: "bonus", label: "Bubble bonus rounds (Yellow Diamonds, times tables, fractions, maps…)" },
  { id: "rhythmTap", group: "bonus", label: "Rhythm Tap bonus" },
];

let sounds = {};
const listeners = new Set();
let started = false;

const start = () => {
  if (started || typeof window === "undefined") return;
  started = true;
  let unsub = null;
  onAuthStateChanged(getAuth(), (user) => {
    if (unsub) {
      unsub();
      unsub = null;
    }
    if (!user) return;
    unsub = onSnapshot(
      doc(db, "rewardSettings", "main"),
      (snap) => {
        sounds = (snap.exists() && snap.data()?.sounds) || {};
        // load the uploaded files now, so the first tap isn't late
        Object.values(sounds.all || {})
          .concat(...Object.values(sounds.games || {}).map((g) => Object.values(g || {})))
          .forEach((s) => {
            if (!s?.url) return;
            poolFor(s.url);
            preloadFast(s.url);
          });
        listeners.forEach((f) => f(sounds));
      },
      () => {}
    );
  });
};

export const getGameSounds = () => sounds;
export const onGameSounds = (fn) => {
  listeners.add(fn);
  start();
  return () => listeners.delete(fn);
};

/** What is set for this slot: { url, volume } | { none: true } | null (use built-in). */
export const soundFor = (slot, game) => {
  const own = game && sounds.games?.[game]?.[slot];
  if (own && (own.url || own.none)) return own;
  const all = sounds.all?.[slot];
  if (all && (all.url || all.none)) return all;
  return null;
};

/* ---------- playing: a small pool per file so quick taps overlap ---------- */

const pools = new Map();
function poolFor(url) {
  if (typeof Audio === "undefined") return null;
  if (!pools.has(url)) {
    const list = Array.from({ length: 4 }, () => {
      const a = new Audio(url);
      a.preload = "auto";
      return a;
    });
    pools.set(url, { list, i: 0 });
  }
  return pools.get(url);
}

export const playUrl = (url, volume = 1) => {
  // Web Audio first: starting an <audio> element on every tap made the
  // bonus rounds stutter on phones (components/quizzes/sfx.js)
  if (playFast(url, Math.max(0, Math.min(1, Number(volume) || 0)))) return;
  const p = poolFor(url);
  if (!p) return;
  const a = p.list[p.i];
  p.i = (p.i + 1) % p.list.length;
  try {
    a.volume = Math.max(0, Math.min(1, Number(volume) || 0));
    a.currentTime = 0;
    a.play().catch(() => {});
  } catch {
    /* autoplay policy */
  }
};

/**
 * Plays the sound for a moment in a game.
 *   slot      one of SOUND_SLOTS' ids
 *   game      one of SOUND_GAMES' ids (or "" for every game only)
 *   fallback  the game's built-in sound: a function that plays it, a url,
 *             or nothing (silent unless the admin uploaded one)
 */
export const playGameSound = (slot, game, fallback) => {
  start();
  const s = soundFor(slot, game);
  if (s?.none) return;
  if (s?.url) {
    playUrl(s.url, s.volume ?? 1);
    return;
  }
  if (typeof fallback === "function") fallback();
  else if (typeof fallback === "string" && fallback) playUrl(fallback, 1);
};

// listen from app start, so the first game already has the sounds
if (typeof window !== "undefined") setTimeout(start, 0);

export default playGameSound;
